import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat, SINGLE_PLAYER } from '../bridge/seats.js';
import { FLOWER_FACES, type Face, PLAIN_FACES, SEASON_FACES } from '../mahjong-solitaire/tiles.js';
import { seededRandom, shuffled } from '../trick/rng.js';
import { MIN_FAAN, type MahjongMeld, type MahjongScore, type MahjongWind, scoreHand } from './score.js';
import { chowPairs, isBonusFace, sortFaces } from './tiles.js';

/**
 * FOUR-PLAYER MAHJONG, Hong Kong style, NO betting, points only. These are the exact rules implemented. They are recalled from general
 * knowledge, not checked against a rulebook; a person who plays must review them (mahjong.md section 4, slice M4).
 *
 *  TILES   136 tiles: dots, bamboo and characters 1-9 (4 each), the four winds and three dragons (4 each). With the `flowers` option (off by
 *          default, "clean" beginner mode) the 4 flowers and 4 seasons are added (144): a bonus tile is set aside the moment it is drawn,
 *          a replacement is taken from the end of the wall, and each one is worth 1 faan.
 *  SEATS   Four seats N, E, S, W; play goes clockwise in that order. The dealer is the East wind, the next seat clockwise South, then West,
 *          then North. The round wind is East unless an option says otherwise.
 *  DEAL    13 tiles each; the dealer then draws a 14th and discards first. The last 14 tiles of the wall (the "dead wall") are never drawn
 *          from the front: a normal draw needs more than 14 tiles left, otherwise the hand is a DRAW (nobody scores).
 *  TURN    A player draws the next tile (this happens automatically), then does ONE of: declare a self-drawn win if the hand is complete and
 *          scores at least MIN_FAAN; declare a kong (four of a kind in hand, or the fourth tile of an exposed pung) and take a replacement from the
 *          end of the wall; or discard one tile face up.
 *  CLAIMS  After a discard each other player may claim it, in this priority: a WIN beats a PUNG or KONG, which beats a CHOW. Players are asked
 *          in that order (ties by turn order after the discarder); the first to claim takes the tile and the rest are not asked.
 *            PUNG  three of a kind, using two tiles from the hand, from any player.
 *            KONG  four of a kind, using three tiles from the hand, from any player; take a replacement tile, then discard.
 *            CHOW  three in a row of one suit, using two tiles from the hand, ONLY from the player whose turn came just before yours (on your left).
 *            WIN   the discard completes a hand that scores at least MIN_FAAN.
 *          After a pung, kong or chow the claimer discards (a claimed tile cannot make a self-drawn win). A claimed tile leaves the discard pile.
 *          Honour and suited tiles are claimed the same way; bonus tiles are never discarded. Robbing a kong and "last tile" bonuses are not played.
 *  WIN     Four sets (chows, pungs or kongs) and a pair. Seven pairs and the other special hands are NOT played. A hand must score at least
 *          MIN_FAAN (see score.ts); a complete hand with no scoring feature may not win and play goes on.
 *  SCORE   The winner scores POINTS_BY_FAAN[faan], faan capped at FAAN_CAP. Nobody loses points. The dealer keeps the deal after a win by the
 *          dealer or a draw hand; otherwise the deal passes clockwise. A game is first to MAHJONG_TARGET points.
 *
 * The state holds every hand and the wall; clients and robots read only `mahjongViewFor` (view.ts).
 */
export type MahjongSeat = Seat;

export class IllegalMahjongMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalMahjongMoveError';
  }
}

/** Tiles left that a normal draw may not take: when the wall is down to this many, the hand is a draw. */
export const DEAD_WALL = 14;
export const MAHJONG_TARGET = 100;
export const WINDS: readonly MahjongWind[] = ['east', 'south', 'west', 'north'];

/** `discard`: the seat in `current` must act (win, kong or discard); `claim`: the seat in `turn` decides whether to claim the last discard. */
export type MahjongPhase = 'discard' | 'claim' | 'done';

export interface Discarded {
  readonly tile: Face;
  readonly from: Seat;
}

export interface PendingClaim {
  readonly tile: Face;
  readonly from: Seat;
  /** The seats still to decide, in the order they are asked. The first is `turn`. */
  readonly queue: readonly Seat[];
}

export interface MahjongResult {
  /** The winner, or null for a draw hand. */
  readonly winner: Seat | null;
  readonly selfDrawn: boolean;
  /** Who threw the winning tile, for a win on a discard. */
  readonly from: Seat | null;
  readonly score: MahjongScore | null;
  readonly points: number;
}

export interface MahjongGame {
  readonly seed: number;
  readonly dealer: Seat;
  readonly roundWind: MahjongWind;
  readonly flowers: boolean;
  readonly controllers: BridgeControllers;
  readonly hands: Readonly<Record<Seat, readonly Face[]>>;
  readonly melds: Readonly<Record<Seat, readonly MahjongMeld[]>>;
  /** Flowers and seasons set aside by each seat (empty unless the flowers option is on). */
  readonly bonus: Readonly<Record<Seat, readonly Face[]>>;
  /** Face-down tiles: the next draw is the first, replacements come off the end. */
  readonly wall: readonly Face[];
  /** Every tile discarded and not claimed, in order. All of it is public. */
  readonly discards: readonly Discarded[];
  readonly phase: MahjongPhase;
  /** Whose turn it is to draw and discard. */
  readonly current: Seat;
  /** The seat that must act now: `current`, or the next seat in the claim queue. */
  readonly turn: Seat;
  readonly pending: PendingClaim | null;
  /** The tile `current` just drew (or took as a replacement); null after a claim. A win declared with a drawn tile is a self-drawn win. */
  readonly lastDrawn: Face | null;
  readonly result: MahjongResult | null;
}

export interface NewMahjongOptions {
  readonly seed: number;
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
  readonly flowers?: boolean;
  readonly roundWind?: MahjongWind;
}

const nextSeat = (s: Seat): Seat => BRIDGE_SEATS[(BRIDGE_SEATS.indexOf(s) + 1) % 4] as Seat;
const prevSeat = (s: Seat): Seat => BRIDGE_SEATS[(BRIDGE_SEATS.indexOf(s) + 3) % 4] as Seat;
const distance = (from: Seat, to: Seat): number => (BRIDGE_SEATS.indexOf(to) - BRIDGE_SEATS.indexOf(from) + 4) % 4;

/** The wind of a seat this hand: the dealer is East, then South, West, North clockwise. */
export function seatWind(game: Pick<MahjongGame, 'dealer'>, seat: Seat): MahjongWind {
  return WINDS[distance(game.dealer, seat)] as MahjongWind;
}

const setHand = (game: MahjongGame, seat: Seat, hand: readonly Face[]): MahjongGame => ({ ...game, hands: { ...game.hands, [seat]: sortFaces(hand) } });
const removeOne = (hand: readonly Face[], tile: Face): Face[] => {
  const i = hand.indexOf(tile);
  return i < 0 ? [...hand] : [...hand.slice(0, i), ...hand.slice(i + 1)];
};
const count = (hand: readonly Face[], tile: Face): number => hand.filter((t) => t === tile).length;

/** The tile set for a game: 136 playable tiles, plus 8 bonus tiles with the flowers option. */
export function mahjongTileSet(flowers: boolean): Face[] {
  const tiles: Face[] = [];
  for (const f of PLAIN_FACES) for (let i = 0; i < 4; i++) tiles.push(f);
  if (flowers) tiles.push(...FLOWER_FACES, ...SEASON_FACES);
  return tiles;
}

/**
 * Take one tile for `seat`: from the front of the wall (a normal draw, which needs more than DEAD_WALL tiles left) or from the end (a
 * replacement). A bonus tile is set aside and replaced from the end. Null when the wall cannot supply a tile, which makes the hand a draw.
 */
function drawFor(game: MahjongGame, seat: Seat, fromBack: boolean): MahjongGame | null {
  let g = game;
  let back = fromBack;
  for (;;) {
    if (back ? g.wall.length === 0 : g.wall.length <= DEAD_WALL) return null;
    const tile = (back ? g.wall[g.wall.length - 1] : g.wall[0]) as Face;
    const wall = back ? g.wall.slice(0, -1) : g.wall.slice(1);
    if (isBonusFace(tile)) {
      g = { ...g, wall, bonus: { ...g.bonus, [seat]: [...g.bonus[seat], tile] } };
      back = true;
      continue;
    }
    return setHand({ ...g, wall, lastDrawn: tile }, seat, [...g.hands[seat], tile]);
  }
}

const drawnHand = (game: MahjongGame): MahjongGame => ({ ...game, phase: 'done', pending: null, lastDrawn: null, result: { winner: null, selfDrawn: false, from: null, score: null, points: 0 } });

export function newMahjongGame(opts: NewMahjongOptions): MahjongGame {
  const dealer = opts.dealer ?? 'N';
  const flowers = opts.flowers ?? false;
  const wall = shuffled(mahjongTileSet(flowers), seededRandom(opts.seed));
  const hands: Record<Seat, Face[]> = { N: [], E: [], S: [], W: [] };
  const bonus: Record<Seat, Face[]> = { N: [], E: [], S: [], W: [] };
  const order = BRIDGE_SEATS.map((_, i) => BRIDGE_SEATS[(BRIDGE_SEATS.indexOf(dealer) + i) % 4] as Seat);
  for (let r = 0; r < 13; r++) for (const s of order) hands[s].push(wall.shift() as Face);
  let game: MahjongGame = {
    seed: opts.seed,
    dealer,
    roundWind: opts.roundWind ?? 'east',
    flowers,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    hands,
    melds: { N: [], E: [], S: [], W: [] },
    bonus,
    wall,
    discards: [],
    phase: 'discard',
    current: dealer,
    turn: dealer,
    pending: null,
    lastDrawn: null,
    result: null,
  };
  if (flowers) {
    // Set aside any bonus tiles in the dealt hands and replace them from the end of the wall, in turn order from the dealer.
    for (const s of order) {
      while (game.hands[s].some(isBonusFace)) {
        const tile = game.hands[s].find(isBonusFace) as Face;
        const w = game.wall.slice(0, -1);
        const taken = game.wall[game.wall.length - 1] as Face;
        game = setHand({ ...game, wall: w, bonus: { ...game.bonus, [s]: [...game.bonus[s], tile] } }, s, [...removeOne(game.hands[s], tile), taken]);
      }
    }
  }
  const first = drawFor(game, dealer, false);
  return first ?? drawnHand(game);
}

// ---- what a seat may do ------------------------------------------------------------------------------------------------------------

const scoreContext = (game: MahjongGame, seat: Seat, selfDrawn: boolean) => ({
  selfDrawn,
  seatWind: seatWind(game, seat),
  roundWind: game.roundWind,
  melds: game.melds[seat],
  bonus: game.bonus[seat].length,
});

/** The score of `hand` as a winning hand for `seat`, or null when it is not complete or scores under MIN_FAAN. */
export function winningScore(game: MahjongGame, seat: Seat, hand: readonly Face[], selfDrawn: boolean): MahjongScore | null {
  const s = scoreHand(hand, scoreContext(game, seat, selfDrawn));
  return s && s.faan >= MIN_FAAN ? s : null;
}

/** May the seat to act declare a self-drawn win now? */
export function canSelfWin(game: MahjongGame): boolean {
  if (game.phase !== 'discard' || game.lastDrawn === null) return false;
  return winningScore(game, game.current, game.hands[game.current], true) !== null;
}

/** Tiles the seat to act may declare a kong with: four in hand (concealed kong) or the fourth tile of an exposed pung (extending it). */
export function kongTiles(game: MahjongGame): Face[] {
  if (game.phase !== 'discard' || game.wall.length <= DEAD_WALL) return [];
  const hand = game.hands[game.current];
  const out = new Set<Face>();
  for (const t of hand) if (count(hand, t) === 4) out.add(t);
  for (const m of game.melds[game.current]) if (m.kind === 'pung' && hand.includes(m.tiles[0] as Face)) out.add(m.tiles[0] as Face);
  return [...out];
}

export interface ClaimOptions {
  readonly win: boolean;
  readonly pung: boolean;
  readonly kong: boolean;
  /** The pairs of tiles from the hand that would make a chow with the discard (only the seat after the discarder). */
  readonly chows: readonly (readonly [Face, Face])[];
}

const NO_CLAIM: ClaimOptions = { win: false, pung: false, kong: false, chows: [] };

/** What `seat` could do with the tile `from` just discarded. */
export function claimOptions(game: MahjongGame, seat: Seat, tile: Face, from: Seat): ClaimOptions {
  if (seat === from) return NO_CLAIM;
  const hand = game.hands[seat];
  const n = count(hand, tile);
  return {
    win: winningScore(game, seat, [...hand, tile], false) !== null,
    pung: n >= 2,
    kong: n >= 3 && game.wall.length > DEAD_WALL,
    chows: prevSeat(seat) === from ? chowPairs(hand, tile) : [],
  };
}

export const hasAnyClaim = (o: ClaimOptions): boolean => o.win || o.pung || o.kong || o.chows.length > 0;

/** The options of the seat that has to decide a claim now (the first in the queue), or none. */
export function currentClaimOptions(game: MahjongGame): ClaimOptions {
  if (game.phase !== 'claim' || !game.pending) return NO_CLAIM;
  return claimOptions(game, game.turn, game.pending.tile, game.pending.from);
}

/** The cards the seat to act may discard: any tile in the hand. */
export const mahjongDiscardable = (game: MahjongGame): Face[] => (game.phase === 'discard' ? [...game.hands[game.current]] : []);

// ---- actions -----------------------------------------------------------------------------------------------------------------------

function ensure(cond: boolean, message: string): void {
  if (!cond) throw new IllegalMahjongMoveError(message);
}

/** Hand the turn to the next seat: they draw automatically. A dry wall ends the hand as a draw. */
function nextTurn(game: MahjongGame): MahjongGame {
  const seat = nextSeat(game.current);
  const drawn = drawFor({ ...game, current: seat, turn: seat, pending: null, phase: 'discard' }, seat, false);
  return drawn ?? drawnHand({ ...game, current: seat, turn: seat });
}

/** Win: record the result and end the hand. */
function win(game: MahjongGame, seat: Seat, hand: readonly Face[], selfDrawn: boolean, from: Seat | null): MahjongGame {
  const score = winningScore(game, seat, hand, selfDrawn);
  if (!score) throw new IllegalMahjongMoveError('that hand is not a winning hand');
  return {
    ...setHand(game, seat, hand),
    phase: 'done',
    pending: null,
    lastDrawn: null,
    result: { winner: seat, selfDrawn, from, score, points: score.points },
  };
}

/** Declare a self-drawn win (after drawing, with a complete hand that scores at least MIN_FAAN). */
export function mahjongSelfWin(game: MahjongGame): MahjongGame {
  ensure(game.phase === 'discard', 'you cannot declare a win now');
  ensure(canSelfWin(game), 'your hand is not a winning hand');
  return win(game, game.current, game.hands[game.current], true, null);
}

/** Declare a kong (see `kongTiles`) and take a replacement tile; you then still have to win or discard. */
export function mahjongKong(game: MahjongGame, tile: Face): MahjongGame {
  ensure(game.phase === 'discard', 'you cannot declare a kong now');
  ensure(kongTiles(game).includes(tile), 'you cannot make a kong with that tile');
  const seat = game.current;
  const hand = game.hands[seat];
  const exposedPung = game.melds[seat].find((m) => m.kind === 'pung' && m.tiles[0] === tile);
  let next: MahjongGame;
  if (count(hand, tile) === 4) {
    const rest = hand.filter((t) => t !== tile);
    next = setHand({ ...game, melds: { ...game.melds, [seat]: [...game.melds[seat], { kind: 'kong', tiles: [tile, tile, tile, tile], concealed: true }] } }, seat, rest);
  } else if (exposedPung) {
    next = setHand(
      { ...game, melds: { ...game.melds, [seat]: game.melds[seat].map((m) => (m === exposedPung ? { kind: 'kong' as const, tiles: [tile, tile, tile, tile], concealed: false } : m)) } },
      seat,
      removeOne(hand, tile),
    );
  } else throw new IllegalMahjongMoveError('you cannot make a kong with that tile');
  return drawFor(next, seat, true) ?? drawnHand(next);
}

/** Discard one tile. Anyone who can claim it is asked, in priority order; if nobody can, the next player draws. */
export function mahjongDiscard(game: MahjongGame, tile: Face): MahjongGame {
  ensure(game.phase === 'discard', 'you cannot discard now');
  const seat = game.current;
  ensure(game.hands[seat].includes(tile), 'that tile is not in your hand');
  const moved: MahjongGame = {
    ...setHand(game, seat, removeOne(game.hands[seat], tile)),
    discards: [...game.discards, { tile, from: seat }],
    lastDrawn: null,
  };
  const asked = BRIDGE_SEATS.filter((s) => s !== seat)
    .map((s) => ({ s, o: claimOptions(moved, s, tile, seat) }))
    .filter((x) => hasAnyClaim(x.o))
    .map((x) => ({ s: x.s, rank: x.o.win ? 0 : x.o.pung || x.o.kong ? 1 : 2, d: distance(seat, x.s) }))
    .sort((a, b) => a.rank - b.rank || a.d - b.d)
    .map((x) => x.s);
  if (asked.length === 0) return nextTurn(moved);
  return { ...moved, phase: 'claim', turn: asked[0] as Seat, pending: { tile, from: seat, queue: asked } };
}

function claiming(game: MahjongGame): { pending: PendingClaim; seat: Seat; options: ClaimOptions } {
  ensure(game.phase === 'claim' && game.pending !== null, 'there is nothing to claim');
  const pending = game.pending as PendingClaim;
  return { pending, seat: game.turn, options: claimOptions(game, game.turn, pending.tile, pending.from) };
}

/** Give up the claim: the next seat in the queue is asked, and when nobody is left the next player draws. */
export function mahjongPass(game: MahjongGame): MahjongGame {
  const { pending } = claiming(game);
  const queue = pending.queue.slice(1);
  if (queue.length === 0) return nextTurn({ ...game, pending: null });
  return { ...game, turn: queue[0] as Seat, pending: { ...pending, queue } };
}

/** The claimed tile leaves the pile and the claimer becomes the player to act. */
function takeDiscard(game: MahjongGame, seat: Seat): MahjongGame {
  return { ...game, discards: game.discards.slice(0, -1), phase: 'discard', current: seat, turn: seat, pending: null, lastDrawn: null };
}

/** Claim the last discard as a win. */
export function mahjongClaimWin(game: MahjongGame): MahjongGame {
  const { pending, seat, options } = claiming(game);
  ensure(options.win, 'that tile does not give you a winning hand');
  const base = { ...game, discards: game.discards.slice(0, -1) };
  return win(base, seat, [...game.hands[seat], pending.tile], false, pending.from);
}

export function mahjongClaimPung(game: MahjongGame): MahjongGame {
  const { pending, seat, options } = claiming(game);
  ensure(options.pung, 'you cannot make a pung with that tile');
  const t = pending.tile;
  const hand = removeOne(removeOne(game.hands[seat], t), t);
  const meld: MahjongMeld = { kind: 'pung', tiles: [t, t, t], concealed: false };
  return takeDiscard(setHand({ ...game, melds: { ...game.melds, [seat]: [...game.melds[seat], meld] } }, seat, hand), seat);
}

export function mahjongClaimKong(game: MahjongGame): MahjongGame {
  const { pending, seat, options } = claiming(game);
  ensure(options.kong, 'you cannot make a kong with that tile');
  const t = pending.tile;
  const hand = removeOne(removeOne(removeOne(game.hands[seat], t), t), t);
  const meld: MahjongMeld = { kind: 'kong', tiles: [t, t, t, t], concealed: false };
  const taken = takeDiscard(setHand({ ...game, melds: { ...game.melds, [seat]: [...game.melds[seat], meld] } }, seat, hand), seat);
  return drawFor(taken, seat, true) ?? drawnHand(taken);
}

/** Claim the last discard for a chow using two tiles from the hand (see `ClaimOptions.chows`). */
export function mahjongClaimChow(game: MahjongGame, tiles: readonly [Face, Face]): MahjongGame {
  const { pending, seat, options } = claiming(game);
  const ok = options.chows.some(([a, b]) => (a === tiles[0] && b === tiles[1]) || (a === tiles[1] && b === tiles[0]));
  ensure(ok, 'those tiles do not make a chow with that discard');
  const hand = removeOne(removeOne(game.hands[seat], tiles[0]), tiles[1]);
  const meld: MahjongMeld = { kind: 'chow', tiles: sortFaces([pending.tile, tiles[0], tiles[1]]), concealed: false };
  return takeDiscard(setHand({ ...game, melds: { ...game.melds, [seat]: [...game.melds[seat], meld] } }, seat, hand), seat);
}

// ---- results and scores over a game ------------------------------------------------------------------------------------------------

export function mahjongResult(game: MahjongGame): MahjongResult | null {
  return game.phase === 'done' ? game.result : null;
}

export type MahjongTotals = Readonly<Partial<Record<Seat, number>>>;

/** Scores add up over hands; nobody loses points. A draw hand adds nothing. */
export function addMahjongHand(totals: MahjongTotals, result: MahjongResult): MahjongTotals {
  if (!result.winner) return totals;
  return { ...totals, [result.winner]: (totals[result.winner] ?? 0) + result.points };
}

/** The seat that has reached the target with the highest total, or null while the game goes on (a tie at the top plays another hand). */
export function mahjongGameWinner(totals: MahjongTotals, target = MAHJONG_TARGET): Seat | null {
  const entries = Object.entries(totals).filter((e): e is [Seat, number] => typeof e[1] === 'number');
  const best = Math.max(0, ...entries.map(([, v]) => v));
  if (best < target) return null;
  const leaders = entries.filter(([, v]) => v === best);
  return leaders.length === 1 ? (leaders[0] as [Seat, number])[0] : null;
}

/** Who deals next: the dealer again after their own win or a draw hand, otherwise the next seat clockwise. */
export function nextMahjongDealer(game: MahjongGame): Seat {
  const r = game.result;
  return !r || r.winner === null || r.winner === game.dealer ? game.dealer : nextSeat(game.dealer);
}
